import { fmtMicro, shortAddr, symbolFor } from "./mkt-format";
import { useLandProvenance } from "./useMarketplace";
import { useT } from "../../i18n";
import type { LandProvenanceEvent, MarketplaceConfig } from "../../lib/marketplace-api";

const KIND_LABEL: Record<string, string> = {
  ORDER_FILLED: "marketplace.provenance.resale",
  PRIMARY_PURCHASE: "marketplace.provenance.primaryPurchase",
  PRIVATE_SALE: "marketplace.provenance.privateSale",
};

function kindLabelKey(kind: string): string {
  return KIND_LABEL[kind] ?? "marketplace.provenance.transfer";
}

function fmtWhen(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * IdentityLabel — Macro #29 identity-snapshot law.
 * The wallet address is authoritative. A historical display-name SNAPSHOT is
 * shown when it exists; otherwise the CURRENT profile name may be shown ONLY when
 * explicitly labelled as current — never implied to be historical.
 */
function IdentityLabel({
  wallet,
  snapshot,
  current,
  testId,
}: {
  wallet: string | null;
  snapshot: string | null;
  current: string | null;
  testId: string;
}) {
  const t = useT();
  if (!wallet) return <span data-testid={testId}>—</span>;
  return (
    <span data-testid={testId} className="inline-flex flex-col">
      {snapshot ? (
        <span className="font-display text-xs text-text-primary">{snapshot}</span>
      ) : current ? (
        <span className="font-display text-xs text-text-primary">
          {current}
          <span
            data-testid={`${testId}-current-label`}
            className="ml-1.5 font-mono text-[8.5px] uppercase tracking-[0.14em] text-neon-coral/80"
          >
            {t("marketplace.provenance.currentNameLabel")}
          </span>
        </span>
      ) : null}
      <span className="font-mono text-[10px] text-text-muted">{shortAddr(wallet)}</span>
    </span>
  );
}

function ProvenanceRow({ e, config }: { e: LandProvenanceEvent; config?: MarketplaceConfig | null }) {
  const t = useT();
  const sym = symbolFor(e.currency ?? "", config);
  return (
    <div data-testid="land-provenance-event" data-kind={e.kind} className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span data-testid="land-provenance-kind" className="font-mono text-[10px] tracking-[0.16em] uppercase text-neon-teal">
          {t(kindLabelKey(e.kind))}
        </span>
        <span className="font-mono text-[10px] text-text-muted">{fmtWhen(e.created_at)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-faint">{t("marketplace.provenance.from")}</span>
          <IdentityLabel wallet={e.previous_owner} snapshot={e.seller_display_name_snapshot} current={e.seller_display_name_current} testId="land-provenance-from" />
        </div>
        <svg width="16" height="16" viewBox="0 0 12 12" fill="none" aria-hidden><path d="M3 6h6m0 0l-3-3m3 3l-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="text-text-muted" /></svg>
        <div className="flex flex-col gap-0.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-text-faint">{t("marketplace.provenance.to")}</span>
          <IdentityLabel wallet={e.new_owner} snapshot={e.buyer_display_name_snapshot} current={e.buyer_display_name_current} testId="land-provenance-to" />
        </div>
      </div>
      {e.price_micro != null && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10.5px]">
          <span data-testid="land-provenance-price" className="text-neon-teal">
            {t("marketplace.provenance.price")} {fmtMicro(e.price_micro)} {sym}
          </span>
          {e.grid_fee_micro != null && (
            <span className="text-text-muted">{t("marketplace.provenance.fee")} {fmtMicro(e.grid_fee_micro)}</span>
          )}
          {e.seller_proceeds_micro != null && (
            <span className="text-text-primary">{t("marketplace.provenance.net")} {fmtMicro(e.seller_proceeds_micro)} {sym}</span>
          )}
        </div>
      )}
      {e.tx_hash && (
        <a
          href={`https://sepolia.basescan.org/tx/${e.tx_hash}`}
          target="_blank"
          rel="noreferrer"
          data-testid="land-provenance-tx"
          className="inline-block font-mono text-[10px] text-neon-teal hover:underline"
        >
          {t("marketplace.provenance.viewTx")}
        </a>
      )}
    </div>
  );
}

export function LandProvenanceTimeline({
  assetKey,
  config,
}: {
  assetKey: string | null;
  config?: MarketplaceConfig | null;
}) {
  const t = useT();
  const q = useLandProvenance(assetKey);
  const events = q.data?.events ?? [];
  return (
    <div className="mt-12" data-testid="land-provenance">
      <h2 className="font-display text-lg font-semibold text-text-primary uppercase tracking-[0.02em] mb-4">
        {t("marketplace.provenance.title")}
      </h2>
      {events.length === 0 ? (
        <p data-testid="land-provenance-empty" className="font-mono text-[11px] text-text-muted">
          {t("marketplace.provenance.empty")}
        </p>
      ) : (
        <div className="neon-glass rounded-lg border border-border-divider divide-y divide-border-divider">
          {events.map((e) => (
            <ProvenanceRow key={`${e.seq}-${e.tx_hash ?? e.kind}`} e={e} config={config} />
          ))}
        </div>
      )}
    </div>
  );
}
